import axios from "axios";
import { useGlobalStore } from '@/store/storeGlobal';
import { useEnvironmentStore } from '@/store/environmentStore';
import { getActiveProvider } from '@/helpers/auth/providerRegistry';

// Config-/miljø-modul (task 2677): MSAL-motoren eies av providerne i
// `helpers/auth/` og nås via auth-shellen (`useAuthShell`/providerRegistry) —
// denne modulen har ingen egen auth-flyt. Identitets-/popup-state
// re-eksporteres fordi konsumentene (komponenter, stores, tester) importerer
// fra `@/helpers/auth`. Re-eksport (ikke kopi) bevarer live-bindingene —
// `userAccount` og ref-ene skal peke på samme underliggende state overalt.
export { isPopupBlocked, resetPopupBlockedCache } from '@/helpers/auth/popupFlow';
export { saveIntendedRoute, getAndClearIntendedRoute } from '@/helpers/auth/redirectFallback';
export { userAzureId, userOid, userAccount } from '@/helpers/auth/sessionState';
export { resolveAccountOid } from '@ics/auth-api';
// Entra-klientkonfig (Bug 2672) bor i en avhengighetsfri modul fordi module-bridge-bundelen
// konsumerer samme resolver (én clientId-kilde, task 2678) uten å dra FE-avhengigheter med seg.
export { resolveEntraClientConfig, type EntraClientConfig } from '@ics/auth-api';

// *************************************************
// API ADDRESS
// *************************************************
const debugPrefix = '[Auth]';

// Funksjon for å oppdatere environment store og global store etter Pinia er aktivert
export function updateEnvironmentStore (config: ConfigCache) {
  try {
    const environmentStore = useEnvironmentStore();
    environmentStore.setConfigCache(config);
    environmentStore.setEnvironmentName(config.VITE_ENVIRONMENT || 'DEV');
    // WI 2426: mangler feltet (eldre backend / fallback) → prod (trygg standard).
    environmentStore.setIsProduction(config.isProductionEnvironment ?? true);
    console.log(`${debugPrefix} Environment store oppdatert med:`, config.VITE_ENVIRONMENT);

    // Oppdater global store med build info
    const globalStore = useGlobalStore();
    globalStore.setBuildInfo(config.BUILD_VERSION || '', config.BUILD_DATE || '');
    console.log(`${debugPrefix} Global store oppdatert med build info:`, config.BUILD_VERSION, config.BUILD_DATE);
  } catch (error) {
    console.warn(`${debugPrefix} Kunne ikke oppdatere stores:`, error);
  }
}

// Type definitions
export interface PaletteFromConfig {
  key: string;
  name: string;
  tokens: {
    light: Record<string, string>;
    dark: Record<string, string>;
  };
}

export interface ConfigCache {
  VITE_ENVIRONMENT: string;
  VITE_API_URL: string;
  // Task 2679: /api/config serverer ikke lenger toppnivå-MSAL-felter —
  // authProviders er eneste kilde derfra. Feltene er optional og fylles kun av
  // .env-stien (getEnvConfig) og legacy SWA-function-grenen (VITE_-prefiks),
  // som mater providerRegistry-fallbacken (deriveLegacyEntraConfig).
  VITE_MSAL_CLIENT_ID?: string;
  VITE_MSAL_AUTHORITY?: string;
  VITE_MSAL_SCOPES?: string | string[];
  BUILD_VERSION?: string;
  BUILD_DATE?: string;
  // Multi-provider auth-config (#1196). FE auth-shell (#1197) leser denne
  // for å bygge providere; bevares uendret fra backend-responsen.
  authProviders?: unknown[];
  // Theming-palette (#1208). themeStore plukker denne ved init.
  palette?: PaletteFromConfig;
  // WI 2426: er gjeldende miljø produksjon? Gater ikke-prod-funksjoner i FE (WI 2430).
  isProductionEnvironment?: boolean;
}

// Cache for konfigurasjonsverdier
let configCache: ConfigCache | null = null;
let configPromise: Promise<ConfigCache> | null = null;
let environment: string | null = null;

/**
 * Theming (#1210): bygger URL til /api/config med paletteOverride-query-param
 * hvis (a) bygget er DEV og (b) URL-en har `?paletteOverride=<key>`. Tillater
 * lokal palette-test uten redeploy. Backend honorerer kun paramet når
 * IHostEnvironment.IsDevelopment() er true.
 */
// A palette key is an identifier from the design system - one or more identifier segments joined by
// a slash, never free text. The value arrives from the address bar, so it is validated here rather
// than only percent-encoded. Anything carrying traversal, query, fragment or protocol characters is
// dropped and never reaches the request URL at all.
const PALETTE_OVERRIDE_PATTERN = /^[\w-]+(?:\/[\w-]+)*$/;
const PALETTE_OVERRIDE_MAX_LENGTH = 64;

export function buildConfigUrlWithPaletteOverride (baseUrl: string): string {
  if (!import.meta.env.DEV) return baseUrl;
  if (typeof window === 'undefined') return baseUrl;

  const params = new URLSearchParams(window.location.search);
  const override = params.get('paletteOverride');
  if (!override) return baseUrl;
  if (override.length > PALETTE_OVERRIDE_MAX_LENGTH) return baseUrl;
  if (!PALETTE_OVERRIDE_PATTERN.test(override)) return baseUrl;

  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}paletteOverride=${encodeURIComponent(override)}`;
}

// Funksjon for å hente default-konfigurasjon fra Vite environment variabler (.env)
function getEnvConfig (): ConfigCache {
  const clientId = import.meta.env.VITE_MSAL_CLIENT_ID || '';

  let scopes: string | string[] = import.meta.env.VITE_MSAL_SCOPES || '';
  if (!scopes && clientId) {
    scopes = [`api://${clientId}/.default`];
  }

  return {
    VITE_ENVIRONMENT: import.meta.env.VITE_ENVIRONMENT || 'DEV',
    VITE_API_URL: import.meta.env.VITE_API_URL || import.meta.env.VITE_API_URI_DEV?.replace(/\/api\/?$/, '') || 'https://localhost:7047',
    VITE_MSAL_CLIENT_ID: clientId,
    VITE_MSAL_AUTHORITY: import.meta.env.VITE_MSAL_AUTHORITY || '',
    VITE_MSAL_SCOPES: scopes
  };
}

// Normaliserer API-respons til ConfigCache format
// Håndterer camelCase (apiUrl), PascalCase (ApiUrl) og VITE_-prefix varianter
/**
 * The field names a config response carried, for diagnostics. Names only - the values are user
 * controlled and never belong in a log (tssecurity:S5145). An empty or non-object body reads as
 * "(ingen felter)" so the warning still says something.
 */
function configResponseFieldNames (data: unknown): string {
  if (data === null || typeof data !== 'object') return '(ingen felter)';
  const names = Object.keys(data as Record<string, unknown>);
  return names.length > 0 ? names.join(', ') : '(ingen felter)';
}

function normalizeApiResponse (data: Record<string, unknown>): ConfigCache | null {
  // Prøv camelCase/PascalCase først (ny standard fra .NET API).
  // Task 2679: /api/config har ingen toppnivå-MSAL-felter lenger — apiUrl er
  // eneste påkrevde felt; påloggingsoppsettet ligger i authProviders.
  const apiUrl = (data.apiUrl || data.ApiUrl) as string | undefined;
  const env = (data.environment || data.Environment) as string | undefined;
  const buildVersion = (data.buildVersion || data.BuildVersion) as string | undefined;
  const buildDate = (data.buildDate || data.BuildDate) as string | undefined;
  // Multi-provider auth-config (#1196): bevares til FE auth-shell (#1197).
  const authProviders = (data.authProviders || data.AuthProviders) as unknown[] | undefined;
  // Theming-palette (#1208): plukkes av themeStore ved init.
  const palette = (data.palette || data.Palette) as PaletteFromConfig | undefined;
  // WI 2426: prod/ikke-prod-klassifisering for gjeldende miljø.
  const isProductionEnvironment = (data.isProductionEnvironment ?? data.IsProductionEnvironment) as boolean | undefined;

  if (apiUrl) {
    return {
      VITE_API_URL: apiUrl,
      VITE_ENVIRONMENT: env || 'unknown',
      BUILD_VERSION: buildVersion,
      BUILD_DATE: buildDate,
      authProviders,
      palette,
      isProductionEnvironment
    };
  }

  // Fallback til VITE_-prefix (legacy SWA function)
  const viteApiUrl = (data.VITE_API_URL || data.vite_API_URL) as string | undefined;
  const viteMsalClientId = (data.VITE_MSAL_CLIENT_ID || data.vite_MSAL_CLIENT_ID) as string | undefined;
  const viteMsalAuthority = (data.VITE_MSAL_AUTHORITY || data.vite_MSAL_AUTHORITY) as string | undefined;
  const viteMsalScopes = (data.VITE_MSAL_SCOPES || data.vite_MSAL_SCOPES) as string | string[] | undefined;
  const viteEnv = (data.VITE_ENVIRONMENT || data.vite_ENVIRONMENT) as string | undefined;
  // Build info: sjekk både camelCase (SWA function serialiserer med camelCase) og VITE_ prefix
  const viteBuildVersion = (data.buildVersion || data.BuildVersion || data.VITE_BUILD_VERSION || data.vite_BUILD_VERSION) as string | undefined;
  const viteBuildDate = (data.buildDate || data.BuildDate || data.VITE_BUILD_DATE || data.vite_BUILD_DATE) as string | undefined;

  if (viteApiUrl && viteMsalClientId && viteMsalAuthority) {
    return {
      VITE_API_URL: viteApiUrl,
      VITE_ENVIRONMENT: viteEnv || 'unknown',
      VITE_MSAL_CLIENT_ID: viteMsalClientId,
      VITE_MSAL_AUTHORITY: viteMsalAuthority,
      VITE_MSAL_SCOPES: viteMsalScopes || '',
      BUILD_VERSION: viteBuildVersion,
      BUILD_DATE: viteBuildDate
    };
  }

  return null;
}

// Retry mot /api/config ved app-boot (#1359). Et transient blaff (container
// cold-start / Front Door-timeout rett etter deploy) skal ikke gi feil drakt +
// "dev (local)" — vi prøver på nytt med backoff, kappet på total tid slik at
// login-skjermen fortsatt vises raskt hvis backend genuint er nede.
const CONFIG_MAX_ATTEMPTS = 4; // 1 initialforsøk + 3 retries
const CONFIG_RETRY_BASE_MS = 400;
const CONFIG_RETRY_MAX_TOTAL_MS = 3500;
// Stram per-forsøk-timeout ved boot (#1359): en hengende tilkobling skal ikke
// holde login-skjermen igjen i 10s × forsøk. 4s gir rikelig margin for et
// friskt /api/config-svar mens worst-case ved full heng forblir bundet.
const CONFIG_ATTEMPT_TIMEOUT_MS = 4000;

// Transient = ingen respons (nettverk/timeout) eller 5xx. 4xx er ikke transient
// (retry hjelper ikke) og kastes umiddelbart.
function isTransientConfigError (error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (typeof status === 'number') {
    return status >= 500;
  }
  return true;
}

async function fetchConfigWithRetry (configUrl: string) {
  const startedAt = Date.now();
  let lastError: unknown;

  for (let attempt = 0; attempt < CONFIG_MAX_ATTEMPTS; attempt++) {
    try {
      return await axios.get(configUrl, { timeout: CONFIG_ATTEMPT_TIMEOUT_MS });
    } catch (error) {
      lastError = error;
      if (!isTransientConfigError(error) || attempt === CONFIG_MAX_ATTEMPTS - 1) {
        break;
      }
      const backoffMs = CONFIG_RETRY_BASE_MS * 2 ** attempt;
      if (Date.now() - startedAt + backoffMs > CONFIG_RETRY_MAX_TOTAL_MS) {
        break;
      }
      console.warn(`${debugPrefix} /api/config-forsøk ${attempt + 1} feilet (transient) — retry om ${backoffMs}ms`);
      await new Promise<void>((resolve) => setTimeout(resolve, backoffMs));
    }
  }

  throw lastError;
}

async function getEnvironmentVariables (): Promise<ConfigCache> {
  // Returner cachet verdi hvis tilgjengelig
  if (configCache !== null) {
    return configCache;
  }

  // Returner eksisterende promise hvis en forespørsel allerede er i gang
  if (configPromise !== null) {
    return configPromise;
  }

  // Opprett en ny promise for API-kallet
  // typescript:S6544: an async promise executor swallows anything it throws before reject() is
  // reached, and the promise then never settles - boot would hang instead of failing. An async
  // function returns the same promise without that hazard.
  const pending = (async (): Promise<ConfigCache> => {
    try {
      console.log(`${debugPrefix} Henter konfigurasjon fra /api/config...`);
      // Theming (#1210): i DEV-build leses ?paletteOverride=<key> fra URL og
      // sendes til /api/config slik at utvikler kan teste paletter uten
      // restart eller deploy. Backend honorerer kun query-paramet i
      // Development-miljø (verifisert serverside).
      const configUrl = buildConfigUrlWithPaletteOverride('/api/config');
      // Retry på transient feil ved boot (#1359) før vi gir opp.
      const response = await fetchConfigWithRetry(configUrl);

      // Normaliser API-responsen
      const normalized = normalizeApiResponse(response.data);

      if (normalized) {
        configCache = normalized;
        environment = normalized.VITE_ENVIRONMENT;
        applyApiBasePath(normalized.VITE_API_URL);
        console.log(`${debugPrefix} Konfigurasjon hentet fra API:`, {
          environment: normalized.VITE_ENVIRONMENT,
          apiUrl: normalized.VITE_API_URL,
          authProviders: normalized.authProviders?.length ?? 0
        });
        return normalized;
      }

      // tssecurity:S5145: the response body is user controlled and must not reach the log verbatim.
      // What the warning is for is telling WHICH required fields were missing, so it logs the field
      // names the response carried - never their values.
      console.warn(
        `${debugPrefix} API-respons mangler påkrevde felter, faller tilbake til .env`,
        { feltIRespons: configResponseFieldNames(response.data) }
      );
    } catch (error) {
      // API ikke tilgjengelig (timeout, nettverksfeil, 5xx) — IKKE fallback til
      // .env her. Det skjuler at backenden er nede og gir villedende UX
      // (provider-knappen virker, men API-kall feiler umiddelbart etter login).
      // La kaller (auth-shell) håndtere feilen og vise "kan ikke koble til
      // server"-melding pre-login.
      console.warn(`${debugPrefix} Kunne ikke hente konfigurasjon fra API:`, error);
      configPromise = null;
      throw error;
    }

    // Fallback til .env — kjører kun hvis API svarte men returnerte ufullstendig data.
    const envConfig = getEnvConfig();
    if (envConfig.VITE_MSAL_CLIENT_ID && envConfig.VITE_MSAL_AUTHORITY) {
      configCache = envConfig;
      environment = envConfig.VITE_ENVIRONMENT;
      applyApiBasePath(envConfig.VITE_API_URL);
      console.log(`${debugPrefix} Bruker konfigurasjon fra .env:`, {
        environment: envConfig.VITE_ENVIRONMENT,
        apiUrl: envConfig.VITE_API_URL,
        clientId: envConfig.VITE_MSAL_CLIENT_ID.substring(0, 8) + '...'
      });
    } else {
      console.error(`${debugPrefix} Ingen gyldig konfigurasjon tilgjengelig`);
      // Returner tom config - vil feile senere med bedre feilmelding
    }

    configPromise = null;
    return envConfig;
  })();

  // Bug 3412: marker den lagrede promisen som HAANDTERT. Feiler /api/config, tommer catch-blokka
  // over cachen (configPromise = null) og avviser - men avvisningen kommer fra en retry-loekke med
  // setTimeout-backoff, altsaa etter at kalleren som startet boot-en kan vaere borte. Uten en
  // handler blir den da en uhaandtert rejection. I testkjoeringer felte den hele FE-gaten naar
  // vitest rakk aa tilskrive den et testfil-vindu - og slapp gjennom ellers. Samme rejection i
  // hver kjoering; kun attribusjonen var et lodd.
  //
  // Denne no-op-handleren endrer INGEN adferd for kallere: `pending` returneres uendret, saa den
  // som venter faar fortsatt avvisningen sin. Den sier bare til runtime at noen tar ansvar.
  void pending.catch(() => undefined);
  configPromise = pending;

  return configPromise;
}

// API-basepath for auth-relaterte kall. Utledes ved config-resolusjon
// (getEnvironmentVariables) — boot-en har ingen egen auth-init som setter den
// (task 2677); '/api' er lokal fallback til config er hentet.
let apiBasePath = '/api';

/**
 * Bestem API-path fra konfigurasjonen:
 * - Relativ path (starter med /) → brukes direkte (ACA/Aspire med Front Door)
 * - Absolutt URL i ikke-lokale bygg → `<host>/api`-suffiks (legacy-miljø uten proxy)
 * - Tom/undefined eller lokal dev (Vite-proxy) → '/api'
 */
function applyApiBasePath (apiUrl: string | undefined): void {
  const isLocalDev = import.meta.env.DEV || import.meta.env.MODE === 'development';
  if (apiUrl?.startsWith('/')) {
    apiBasePath = apiUrl;
  } else if (apiUrl && !isLocalDev) {
    apiBasePath = `${apiUrl}/api`;
  } else {
    apiBasePath = '/api';
  }
}

/**
 * API-basepath for uautentiserte/auth-relaterte kall (samme base som global-admin-sjekken bruker).
 * Brukes av auth-audit client-event-rapporteringen (WI 2211).
 */
export function getApiBasePath (): string {
  return apiBasePath;
}

async function getAuthorizedHeaders (): Promise<Record<string, string>> {
  // Token via aktiv provider i auth-shell — shell-boot (initProviderRegistry i
  // main.js) har initialisert registryen før noen probe kan kjøre. Mangler
  // aktiv provider likevel (config/registry kom aldri opp) kastes det — aldri
  // en parallell MSAL-sti (task 2677).
  //
  // WI 2220: SILENT-ONLY. Disse global-admin-probene kjøres i boot-/setup-flyt uten en
  // garantert user-gesture; et popup-fallback her ville bli taust blokkert av nettleseren og
  // bidra til det uventede påloggingsvinduet. Mangler silent-token kastes det → kalleren
  // degraderer til «ikke admin» / «ikke claimet» (sikker default), uten popup.
  const provider = getActiveProvider();
  if (!provider) {
    throw new Error('Ingen aktiv auth-provider — kan ikke hente token');
  }
  const tokenResponse = await provider.getTokenSilent();
  if (!tokenResponse?.accessToken) {
    throw new Error('Kunne ikke hente gyldig token');
  }
  return { Authorization: `Bearer ${tokenResponse.accessToken}` };
}

export interface GlobalAdminCheckResponse {
  isGlobalAdmin: boolean;
  setupSessionId?: string | null;
}

async function checkGlobalAdminStatus (): Promise<GlobalAdminCheckResponse> {
  try {
    const headers = await getAuthorizedHeaders();
    const res = await axios.get(`${apiBasePath}/user/global-admin/check`, { headers });
    return res?.data ?? { isGlobalAdmin: false, setupSessionId: null };
  } catch (error) {
    console.error('[Auth] Feil ved sjekk av GlobalAdmin status:', error);
    return { isGlobalAdmin: false, setupSessionId: null };
  }
}

async function isUserGlobalAdmin (): Promise<boolean> {
  const result = await checkGlobalAdminStatus();
  return result.isGlobalAdmin;
}

async function claimFirstGlobalAdmin (sessionId: string): Promise<boolean> {
  try {
    const headers = await getAuthorizedHeaders();
    await axios.post(`${apiBasePath}/user/global-admin/claim-first`, { setupSessionId: sessionId }, { headers });
    return true;
  } catch (error) {
    console.error('[Auth] Feil ved claim-first GlobalAdmin:', error);
    return false;
  }
}

export {
  isUserGlobalAdmin,
  checkGlobalAdminStatus,
  claimFirstGlobalAdmin,
  environment,
  getEnvironmentVariables
};
